/**
 * «Сохранить для офлайн» у листа и комплекта ПСД (МП-16, в браузере). Своего хранилища нет: данные листа и
 * комплекта запоминает служебный обработчик (public/sw.js) при открытии, файл листа действующей ревизии он
 * сохраняет по просьбе страницы. Здесь — список сохранённого на этом телефоне (что, какая ревизия, когда,
 * сколько места) и правило «ревизия устарела»: в списке одна запись на лист, новая ревизия заменяет старую.
 * Закрытое и не действующее не сохраняется: берётся только ревизия со штампом «В производство работ».
 */

export interface SavedSheet {
  sheetId: string;
  number: string;
  setId: string;
  setCode: string;
  revisionId: string;
  revision: string;
  fileId: string;
  bytes: number;
  savedAt: number;
}

type Scope = { tenantId: string; userId: string };
const PREFIX = 'epc-saved-sheets:';
const key = (s: Scope) => `${PREFIX}${s.tenantId}:${s.userId}`;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readSaved(scope: Scope): SavedSheet[] {
  try {
    const raw = storage()?.getItem(key(scope));
    const list = raw ? (JSON.parse(raw) as SavedSheet[]) : [];
    return Array.isArray(list) ? list.filter((x) => x && typeof x.fileId === 'string') : [];
  } catch {
    return [];
  }
}

function writeSaved(scope: Scope, list: SavedSheet[]) {
  try {
    storage()?.setItem(key(scope), JSON.stringify(list));
  } catch {
    /* хранилище недоступно — экран работает без списка */
  }
}

/** Стереть списки вместе с запомненным (выход, другой человек на телефоне) */
export function clearSaved() {
  try {
    const s = storage();
    if (!s) return;
    const mine: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) mine.push(k);
    }
    for (const k of mine) s.removeItem(k);
  } catch {
    /* нечего стирать */
  }
}

export type SavedState = 'none' | 'fresh' | 'outdated';

/** Что на телефоне по листу: нет, действующая ревизия или устаревшая (после неё выпущена новая) */
export function savedState(saved: SavedSheet | undefined, activeRevisionId: string | null): SavedState {
  if (!saved) return 'none';
  return activeRevisionId && saved.revisionId === activeRevisionId ? 'fresh' : 'outdated';
}

export function filePath(fileId: string) {
  return `/api/v1/psd/files/${fileId}`;
}

type Messenger = (message: { type: 'save-file' | 'drop-file'; url: string }) => Promise<{
  ok: boolean;
  bytes?: number;
  error?: string;
}>;

/** Сообщение обработчику; без обработчика (режим разработки, старый браузер) сохранить нельзя */
export const swMessenger: Messenger = async (message) => {
  const reg = typeof navigator !== 'undefined' ? await navigator.serviceWorker?.ready : undefined;
  const worker = reg?.active;
  if (!worker) return { ok: false, error: 'no_worker' };
  if (message.type === 'drop-file') {
    worker.postMessage(message);
    return { ok: true };
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve({ ok: false, error: 'timeout' }), 5 * 60 * 1000);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve(e.data);
    };
    worker.postMessage(message, [channel.port2]);
  });
};

/**
 * Данные комплекта и листов читаются заново обычным запросом, чтобы служебный обработчик запомнил их (страница,
 * открытая до его запуска, их не отдала ему). Ошибка чтения не мешает сохранить файлы.
 */
export async function warmData(sheets: Array<{ sheetId: string; setId: string }>): Promise<void> {
  const urls = [
    ...new Set([
      ...sheets.map((s) => `/api/v1/psd/sets/${s.setId}`),
      ...sheets.map((s) => `/api/v1/psd/sheets/${s.sheetId}`),
    ]),
  ];
  for (const url of urls) {
    try {
      await fetch(url, { credentials: 'same-origin', headers: { accept: 'application/json' } });
    } catch {
      /* связи нет — файлы тоже не скачаются, об этом скажет сохранение */
    }
  }
}

export interface SheetToSave {
  sheetId: string;
  number: string;
  setId: string;
  setCode: string;
  revisionId: string;
  revision: string;
  fileId: string;
  fileName: string | null;
}

export const SAVE_ERRORS: Record<string, string> = {
  no_worker: 'Работа без связи в этом браузере не включена: откройте портал ещё раз и повторите',
  too_big: 'Лист слишком большой для телефона — откройте его при связи',
  not_pdf: 'Без связи сохраняются только листы в PDF',
  signed_out: 'Вы вышли из портала, лист не сохранён',
  timeout: 'Лист не успел скачаться — повторите при хорошей связи',
};

/**
 * Сохранить листы (файл действующей ревизии) и заменить прежние записи по этим же листам: старый файл убирается.
 * Ошибка одного листа не отменяет остальные. Возвращает, что сохранилось и что нет.
 */
export async function saveSheets(
  scope: Scope,
  sheets: SheetToSave[],
  send: Messenger = swMessenger,
  now: () => number = Date.now,
): Promise<{ saved: SavedSheet[]; failed: Array<{ number: string; error: string }>; bytes: number }> {
  const saved: SavedSheet[] = [];
  const failed: Array<{ number: string; error: string }> = [];
  let list = readSaved(scope);
  const done = new Map<string, number>();
  for (const sh of sheets) {
    if (sh.fileName && !sh.fileName.toLowerCase().endsWith('.pdf')) {
      failed.push({ number: sh.number, error: 'not_pdf' });
      continue;
    }
    // Один PDF на несколько листов скачивается и считается один раз
    const had = done.get(sh.fileId);
    const r =
      had !== undefined
        ? { ok: true, bytes: 0 }
        : await send({ type: 'save-file', url: filePath(sh.fileId) }).catch(() => ({
            ok: false as const,
            error: 'no_worker',
          }));
    if (r.ok && had === undefined) done.set(sh.fileId, r.bytes ?? 0);
    if (!r.ok) {
      failed.push({ number: sh.number, error: r.error ?? 'unknown' });
      continue;
    }
    const old = list.find((x) => x.sheetId === sh.sheetId);
    // Новая ревизия заменяет старую: файл прежней ревизии убирается, если им не пользуется другая запись
    list = list.filter((x) => x.sheetId !== sh.sheetId);
    if (old && old.fileId !== sh.fileId && !list.some((x) => x.fileId === old.fileId))
      await send({ type: 'drop-file', url: filePath(old.fileId) }).catch(() => undefined);
    const item: SavedSheet = {
      sheetId: sh.sheetId,
      number: sh.number,
      setId: sh.setId,
      setCode: sh.setCode,
      revisionId: sh.revisionId,
      revision: sh.revision,
      fileId: sh.fileId,
      bytes: r.bytes ?? 0,
      savedAt: now(),
    };
    list.push(item);
    saved.push(item);
  }
  writeSaved(scope, list);
  return { saved, failed, bytes: saved.reduce((n, x) => n + x.bytes, 0) };
}

/** Убрать лист с телефона */
export async function removeSheet(scope: Scope, sheetId: string, send: Messenger = swMessenger) {
  const list = readSaved(scope);
  const old = list.find((x) => x.sheetId === sheetId);
  if (!old) return;
  const rest = list.filter((x) => x.sheetId !== sheetId);
  writeSaved(scope, rest);
  if (!rest.some((x) => x.fileId === old.fileId))
    await send({ type: 'drop-file', url: filePath(old.fileId) }).catch(() => undefined);
}

/** Какая ревизия листа действующая на сервере сейчас (null — не удалось узнать) */
async function activeRevisionNow(sheetId: string): Promise<string | null | undefined> {
  try {
    const res = await fetch(`/api/v1/psd/sheets/${sheetId}`, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    if (!res.ok || res.headers.get('x-epc-offline')) return undefined;
    const d = (await res.json()) as { versions?: Array<{ revisionId: string; status: string }> };
    return d.versions?.find((v) => v.status === 'production')?.revisionId ?? null;
  } catch {
    return undefined;
  }
}

/**
 * Оставить только те листы, чья ревизия всё ещё действующая на сервере: страница могла устареть, пока человек
 * нажимал кнопку. Если связь не позволила узнать — лист берётся как есть.
 */
export async function onlyStillActive<T extends { sheetId: string; revisionId: string }>(
  sheets: T[],
  now: (id: string) => Promise<string | null | undefined> = activeRevisionNow,
): Promise<{ ok: T[]; changed: T[] }> {
  const ok: T[] = [];
  const changed: T[] = [];
  for (const sh of sheets) {
    const v = await now(sh.sheetId);
    (v === undefined || v === sh.revisionId ? ok : changed).push(sh);
  }
  return { ok, changed };
}

/**
 * Сверить список с тем, что на самом деле лежит в копии: после выхода и входа другого человека файлы стёрты, а
 * список остался бы и обещал лишнее. Возвращает актуальный список.
 */
export async function reconcileSaved(
  scope: Scope,
  hasFile: (fileId: string) => Promise<boolean> = async (id) => {
    try {
      const cache = await caches.open('epc-site-data-v1');
      return Boolean(await cache.match(new Request(filePath(id))));
    } catch {
      return false;
    }
  },
): Promise<SavedSheet[]> {
  const list = readSaved(scope);
  const keep: SavedSheet[] = [];
  for (const x of list) if (await hasFile(x.fileId)) keep.push(x);
  if (keep.length !== list.length) writeSaved(scope, keep);
  return keep;
}
